import type { SbBlokData } from "@storyblok/react/rsc";
import { storyblokApi } from "@/lib/storyblok-api";

import type { Region } from "./region";
import { storyblokCache } from "./storyblok-cache";

export type BlogAsset = { filename?: string; alt?: string; focus?: string };

export type BlogPostContent = {
  component: "blog_post";
  title?: string;
  featured_image?: BlogAsset;
  category?: string;
  categories?: string[];
  featured?: boolean;
  published_date?: string;
  read_time?: number | string;
  body?: unknown;
};

export type BlogPostStory = {
  uuid: string;
  name: string;
  slug: string;
  full_slug?: string;
  first_published_at?: string;
  content: BlogPostContent;
};

export type BlogCategory = { value: string; name: string };

export function postCategories(content?: { category?: string; categories?: string[] }): string[] {
  const all = [content?.category, ...(content?.categories ?? [])];
  return [...new Set(all.filter((c): c is string => Boolean(c)))];
}

export function resolveCategoryName(value: string | undefined, categories: BlogCategory[]): string {
  if (!value) return "";
  return categories.find((c) => c.value === value)?.name ?? "";
}

type DatasourceEntry = { name?: string; value?: string };

export async function fetchBlogCategories(isDraft = false): Promise<BlogCategory[]> {
  try {
    const { data } = await (
      await storyblokApi()
    ).get(
      "cdn/datasource_entries",
      { datasource: "blog_categories", per_page: 1000 },
      storyblokCache(isDraft, ["blog-categories"]),
    );
    const entries = (data?.datasource_entries ?? []) as DatasourceEntry[];
    return entries
      .filter((e): e is BlogCategory => Boolean(e.value && e.name))
      .map((e) => ({ value: e.value, name: e.name }));
  } catch {
    return [];
  }
}

export function extractYouTubeId(url: string | undefined): string | null {
  if (!url) return null;
  const patterns = [
    /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/watch\?v=([a-zA-Z0-9_-]{11})/,
    /youtu\.be\/([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/,
  ];
  for (const p of patterns) {
    const m = url.match(p);
    if (m) return m[1];
  }
  return null;
}

export function absoluteAssetUrl(url?: string): string | undefined {
  if (!url) return undefined;
  if (url.startsWith("//")) return `https:${url}`;
  return url;
}

export function blogPostUrl(slug: string, region: Region): string {
  const clean = slug
    .replace(/^ie\//, "")
    .replace(/^uk\//, "")
    .replace(/^blog\//, "");
  const prefix = region === "uk" ? "/uk" : "";
  return `${prefix}/blog/${clean}/`;
}

export function formatBlogDate(input?: string): string {
  if (!input) return "";
  const d = new Date(input);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

export function estimateReadMinutes(body: unknown): number {
  let words = 0;
  const walk = (node: unknown): void => {
    if (typeof node === "string") {
      words += node.trim().split(/\s+/).filter(Boolean).length;
      return;
    }
    if (Array.isArray(node)) {
      node.forEach(walk);
      return;
    }
    if (node && typeof node === "object") {
      for (const v of Object.values(node as Record<string, unknown>)) walk(v);
    }
  };
  walk(body);
  return Math.max(1, Math.ceil(words / 220));
}

export const BLOG_MODULE_OVERRIDES =
  "[&_section]:bg-white! [&_section>div]:px-0! [&_section>div]:py-0! [&_section>div>div]:flex-col! [&_section>div>div]:items-start! [&_section>div>div]:gap-10!";

export async function fetchBlogModules(isDraft = false): Promise<SbBlokData[]> {
  try {
    const { data } = await (
      await storyblokApi()
    ).get(
      "cdn/stories/settings/blog-modules",
      { version: isDraft ? "draft" : "published" },
      isDraft
        ? { cache: "no-store" }
        : { cache: "force-cache", next: { tags: ["story:settings/blog-modules"] } },
    );
    const body = data?.story?.content?.body;
    return Array.isArray(body) ? (body as SbBlokData[]) : [];
  } catch {
    return [];
  }
}

export async function fetchBlogPosts(region: Region, isDraft = false): Promise<BlogPostStory[]> {
  const localePrefix = region === "uk" ? "uk" : "ie";
  const api = await storyblokApi();
  const all: BlogPostStory[] = [];
  try {
    let page = 1;
    while (true) {
      const { data } = await api.get(
        "cdn/stories",
        {
          starts_with: `${localePrefix}/blog/`,
          filter_query: { component: { in: "blog_post" } },
          sort_by: "content.published_date:desc,first_published_at:desc",
          per_page: 100,
          page,
          version: isDraft ? "draft" : "published",
        },
        storyblokCache(isDraft, [`blog-posts-${region}`]),
      );
      const stories = (data?.stories || []) as BlogPostStory[];
      all.push(...stories);
      if (stories.length < 100) break;
      page++;
      if (page > 20) break;
    }
  } catch {
    return [];
  }
  return all;
}
